import * as React from 'react';
import { useProductBandQuery } from '@/api/product/bands';
import { ArticleProvider } from '@/contexts/ArticleProvider';
import { AuthorProvider } from '@/contexts/AuthorProvider';
import { HighPriorityBlocks, HighPriorityBlocksProvider } from '@/contexts/HighPriorityBlocksProvider';
import { ProductProvider, UseProductHook } from '@/contexts/ProductProvider';
import { useSegmentation } from '@/contexts/SegmentationContext';
import { useGetSettings } from '@/generated/settings';
import { CmsArticle } from '@/types/article';
import { CmsAuthor } from '@/types/author';

export interface BuilderDependenciesProviderProps extends React.PropsWithChildren {
	readonly highPriorityBlocks?: HighPriorityBlocks;
	readonly author?: CmsAuthor;
	readonly article?: CmsArticle;
}

const useProduct: UseProductHook = (productId) => {
	const { segmentationId } = useSegmentation();
	const { data: settings } = useGetSettings(segmentationId);
	const result = useProductBandQuery([productId ?? ''], productId !== null, settings?.data.searchSettings);
	return result.data?.at(0) ?? null;
};

export const BuilderDependenciesProvider: React.FC<BuilderDependenciesProviderProps> = ({
	highPriorityBlocks,
	author,
	article,
	children,
}) => {
	const highPriorityBlocksValue: HighPriorityBlocks = React.useMemo(
		() => highPriorityBlocks ?? new Set(),
		[highPriorityBlocks],
	);

	return (
		<ProductProvider hook={useProduct}>
			<AuthorProvider value={author ?? null}>
				<ArticleProvider value={article ?? null}>
					<HighPriorityBlocksProvider value={highPriorityBlocksValue}>{children}</HighPriorityBlocksProvider>
				</ArticleProvider>
			</AuthorProvider>
		</ProductProvider>
	);
};
